import {Gradient, SceneTitle} from "@client/components/Chrome";
import {useStage} from "@client/components/Stage";
import {JOY_GRADIENT_TOP} from "@client/components/styles";
import {UserMarkdown} from "@client/components/UserMarkdown";
import {useCapture} from "@client/features/camera";
import * as dialogs from "@client/features/dialogs";
import * as journal from "@client/features/journal";
import {shareLink} from "@client/features/sharing";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {server} from "@reactivated";
import {produce} from "immer";
import * as React from "react";
import {match} from "ts-pattern";

export const UpdateJournalEntry = router.UpdateJournalEntry.withHooks((props) => {
    const {day} = props.resolved.journal;

    const entry = day.meals
        .flatMap((meal) => meal.entries)
        .find((entry) => entry.uuid == props.params.entryUUID)!;
    const sourceMeal = day.meals.find((meal) => meal.entries.includes(entry))!;

    const capture = useCapture({
        target: null,
        profile: props.profile,
        cameraEnabled: false,
    });

    const stage = useStage<server.journal.nutrition.ExpandedEntry.output>({
        entries: [entry],
        target: {type: "DAY", day, currentMeal: sourceMeal},
        addIngredients: async () => {
            const payload = stage.validate();

            if (payload == null) {
                return;
            }

            const result = await capture.start({
                initialView: "manual",
                cameraMode: "barcode_only",
            });

            const entries = await match(result)
                .with({type: "barcode"}, async (r) => {
                    const resolved = await capture.resolveBarcode(r.barcode);
                    return resolved?.entries ?? null;
                })
                .with({type: "entries"}, (r) => r.entries)
                .otherwise(() => null);

            if (entries != null) {
                const additionalIngredients = entries.map((entry) => ({
                    ...entry,
                    expanded_recipe: entry.expanded_recipe.map((ingredient) => ({
                        ...ingredient,
                        uuid: crypto.randomUUID(),
                    })),
                    uuid: crypto.randomUUID(),
                }));
                const updated = produce(payload.single, (draft) => {
                    draft.expanded_recipe.push(...additionalIngredients);
                });
                stage.setEntries([updated]);
                void analytics.track("ADDED_INGREDIENTS_TO_RECIPE_ENTRY");
                props.notify("Ingredients added, don't forget to save.");
            }
        },
    });

    return {
        entry,
        stage,
        sourceMeal,
        capture,
    };
})
    .withTab(
        {
            name: "macros",
            icon: "bullseye",
            title: "Macros",
            hasErrors: (props) => props.stage.hasErrors,
        },
        (props) => <Gradient>{props.stage.entries}</Gradient>,
    )
    .withTab(
        {
            icon: "cutlery",
            title: "Ingredients",
            name: "recipe",
            guard: (props) => {
                if (props.stage.ingredients == false) {
                    return false;
                }
                return props;
            },
        },
        (props) => (
            <Gradient>
                {props.capture.dialog}
                {props.stage.ingredients}
                {props.entry.notes != null && (
                    <div className="mt-6 space-y-4">
                        <div className="text-lg font-medium">Instructions</div>
                        <UserMarkdown content={props.entry.notes} />
                    </div>
                )}
            </Gradient>
        ),
    )
    .withActions(() => [])
    .withOptions(({entry, ...props}) => {
        const [remove, expand, share, save]: [
            router.PillAction,
            router.PillAction | null,
            router.PillAction,
            router.PillAction,
        ] = [
            {
                icon: "remove",
                title: "Delete",
                tone: "danger",
                onClick: async () => {
                    const confirmed = await props.dispatch(
                        dialogs.confirm({
                            message: "Are you sure?",
                            body: "This will be deleted forever.",
                        }),
                    );
                    if (confirmed) {
                        void analytics.track("DELETE_JOURNAL_ENTRY_SUCCESS");

                        const updatedJournal = journal.deleteEntryInJournal({
                            journal: props.resolved.journal,
                            entry,
                            deleteMealIfEmpty: true,
                        });
                        void router.push(
                            "journal.journal",
                            {key: updatedJournal.day.key},
                            {journal: updatedJournal},
                        );
                    }
                },
            },
            entry.expanded_recipe.length > 0
                ? {
                      icon: "expand",
                      title: "Expand",
                      onClick: () =>
                          props.dispatch(
                              journal.expandEntry(props.resolved.journal.day, entry),
                          ),
                  }
                : null,
            {
                icon: "share",
                title: "Share",
                onClick: async () => {
                    const result = await server.journal.nutrition.publish_entry({
                        entry_uuid: entry.uuid,
                    });
                    if (result.type === "success") {
                        const outcome = await shareLink(result.data, entry.name);
                        if (outcome === "copied") {
                            props.notify("Link copied.");
                        }
                    }
                },
            },
            {
                icon: "save",
                title: "Save",
                onClick: () => {
                    const payload = props.stage.validate();

                    if (payload != null) {
                        const updatedEntry = payload.single;

                        const updatedDay = produce(
                            props.resolved.journal.day,
                            (draft) => {
                                const sourceMealDraft = draft.meals.find(
                                    (meal) => meal.uuid == props.sourceMeal.uuid,
                                )!;
                                const sourceMealIndex = draft.meals.findIndex(
                                    (m) => m.uuid === sourceMealDraft.uuid,
                                );
                                const sourceIndex = sourceMealDraft.entries.findIndex(
                                    (e) => e.uuid == entry.uuid,
                                );
                                const targetMealDraft = draft.meals.find((meal) =>
                                    journal.mealMatches(meal, payload.meal),
                                );

                                sourceMealDraft.entries.splice(sourceIndex, 1);

                                if (
                                    !journal.mealMatches(props.sourceMeal, payload.meal)
                                ) {
                                    if (targetMealDraft != null) {
                                        // A meal already sits at the new time: merge into
                                        // it rather than spawn a duplicate. If that was
                                        // the source's last entry and the source is an
                                        // auto hour bucket, drop it (deleted server-side
                                        // below); a named meal the user created survives
                                        // emptying — deleting it requires acting on the
                                        // meal itself.
                                        targetMealDraft.entries.push(updatedEntry);

                                        if (
                                            sourceMealDraft.entries.length === 0 &&
                                            sourceMealDraft.type === "AUTO_CREATED"
                                        ) {
                                            draft.meals = draft.meals.filter(
                                                (m) => m.uuid !== sourceMealDraft.uuid,
                                            );
                                        }
                                    } else if (
                                        sourceMealDraft.entries.length === 0 &&
                                        sourceMealDraft.type === "AUTO_CREATED" &&
                                        payload.meal.type === "AUTO_CREATED"
                                    ) {
                                        // No meal at the new time yet: retime the emptied
                                        // auto meal in place so nothing is created or
                                        // deleted.
                                        sourceMealDraft.name = payload.meal.name;
                                        sourceMealDraft.hour = payload.meal.hour;
                                        sourceMealDraft.entries.push(updatedEntry);

                                        draft.meals.splice(sourceMealIndex, 1);
                                        journal.addMealToDayDraft(
                                            draft,
                                            sourceMealDraft,
                                        );
                                    } else {
                                        journal.addMealToDayDraft(draft, {
                                            uuid: crypto.randomUUID(),
                                            share_uuid: "",
                                            key: props.resolved.journal.day.key,
                                            recreate_recipe: null,
                                            name: payload.meal.name,
                                            hour: payload.meal.hour,
                                            entries: [updatedEntry],
                                            type: payload.meal.type,
                                        });
                                    }
                                } else {
                                    sourceMealDraft.entries.splice(
                                        sourceIndex,
                                        0,
                                        updatedEntry,
                                    );
                                }
                            },
                        );

                        const sourceMealRemoved =
                            updatedDay.meals.find(
                                (m) => m.uuid === props.sourceMeal.uuid,
                            ) == null;

                        void server.journal.day.update_day(
                            {user_id: updatedDay.user_id, key: updatedDay.key},
                            {
                                ...updatedDay,
                                deleted_meal_uuids: sourceMealRemoved
                                    ? [props.sourceMeal.uuid]
                                    : [],
                            },
                        );

                        // Dispatch action to track the updated meal
                        const targetMeal = updatedDay.meals.find((meal) =>
                            journal.mealMatches(meal, payload.meal),
                        );

                        props.dispatch({
                            type: "SET_LAST_UPDATED_MEAL",
                            mealUuid: targetMeal?.uuid ?? null,
                        });

                        void router.push(
                            "journal.journal",
                            {key: updatedDay.key},
                            {journal: {...props.resolved.journal, day: updatedDay}},
                        );
                    }
                },
            },
        ];
        return {
            isDelegatable: true,
            title: "Update Entry",
            titleSection: <SceneTitle>Update entry</SceneTitle>,
            back: "Journal",
            scene: {
                kind: "SIMPLE",
                width: "column",
                surface: {kind: "custom", background: JOY_GRADIENT_TOP},
                bottom: {
                    kind: "pills",
                    primary: save,
                    tiles: [remove, ...(expand == null ? [] : [expand]), share],
                },
                corner: props.stage.mealModeToggle,
            },
        };
    });
